import { TFunction } from 'i18next';
import type { Task } from '@bringg/dashboard-sdk/dist/ApiTypes/ApiTypes';
import { OPTIMIZATION_TYPE, Run } from '@bringg/types';
import { ConsecutiveRoutes, RoutesPlannerConfiguration, RoutesPlannerRun } from '@bringg-frontend/routes-planner';
import { dateUtils, isBetween } from '@bringg-frontend/utils';
import { RunStatus } from '@bringg/dashboard-sdk/dist/Run/runs.consts';
import { TasksStoreAdapter } from '@bringg-frontend/global-stores';

import { routesPlannerTasksConverter } from '../../components/routes-planner-modal/services/routes-planner-tasks-converter/routes-planner-tasks-converter';
import { routesPlannerRunConverter } from '../../components/routes-planner-modal/services/routes-planner-run-converter/routes-planner-run-converter';
import RunsStoreAdapter from 'bringg-web/stores/runs/runs-store-adapter';

enum Resource {
	DRIVER = 'DRIVER',
	VEHICLE = 'VEHICLE'
}

async function load(
	selectedRoutes: RoutesPlannerRun[],
	runStore: RunsStoreAdapter,
	tasksStore: TasksStoreAdapter,
	routesPlannerConfig: RoutesPlannerConfiguration,
	readyToExecute: boolean,
	optimizationType: OPTIMIZATION_TYPE,
	t: TFunction
): Promise<ConsecutiveRoutes> {
	const { from, to } = getMinAndMaxTimeOfRuns(selectedRoutes, routesPlannerConfig);

	const selectedRoutesIds: number[] = selectedRoutes.map(({ id }) => id);
	const selectedRuns = await runStore.loadMany({ ids: selectedRoutesIds });

	await runStore.loadMany({
		ready_to_execute: !readyToExecute,
		from_date: from,
		to_date: to,
		run_status: RunStatus.OPEN
	});

	const allConsecutiveRuns = getConsecutiveRuns(selectedRuns, optimizationType, runStore, from, to);

	const consecutiveRoutes = allConsecutiveRuns.map(run =>
		routesPlannerRunConverter.convert(run.id, run, [], {} as Task, true, t)
	);

	const routesTasksIds = allConsecutiveRuns.flatMap(({ task_ids }) => [...task_ids]);
	await tasksStore.loadMany(routesTasksIds);
	const tasksById = getTasksMapById(routesTasksIds, tasksStore);
	const routesById = new Map<number, RoutesPlannerRun>(consecutiveRoutes.map(route => [route.id, route]));

	return {
		routesById,
		tasksById
	};
}

function getConsecutiveRuns(
	selectedRuns: Run[],
	optimizationType: OPTIMIZATION_TYPE,
	runStore: RunsStoreAdapter,
	fromDate: string,
	toDate: string
): Run[] {
	const isCombinedOptimization = optimizationType === OPTIMIZATION_TYPE.COMBINED;

	const driversIdsCached: Set<number> = new Set(selectedRuns.map(run => run.user_id).filter(Boolean));
	const vehiclesIdsCached: Set<number> = new Set(
		isCombinedOptimization ? selectedRuns.map(run => run.vehicle_id).filter(Boolean) : []
	);

	const alreadyLoadedRuns = new Set(selectedRuns.map(run => run.id));
	const loadedRunIds = new Set<number>(alreadyLoadedRuns);

	if (driversIdsCached.size > 0) {
		getConsecutiveRunsByResource(
			driversIdsCached,
			vehiclesIdsCached,
			driversIdsCached,
			vehiclesIdsCached,
			Resource.DRIVER,
			isCombinedOptimization,
			loadedRunIds,
			fromDate,
			toDate,
			runStore
		);
	}
	if (vehiclesIdsCached.size > 0) {
		getConsecutiveRunsByResource(
			driversIdsCached,
			vehiclesIdsCached,
			driversIdsCached,
			vehiclesIdsCached,
			Resource.VEHICLE,
			isCombinedOptimization,
			loadedRunIds,
			fromDate,
			toDate,
			runStore
		);
	}

	return [...loadedRunIds].filter(runId => !alreadyLoadedRuns.has(runId)).map(runId => runStore.get(runId));
}

const getConsecutiveRunsByResource = (
	driverIdsToFetch: Set<number>,
	vehicleIdsToFetch: Set<number>,
	driverIdsCached: Set<number>,
	vehicleIdsCached: Set<number>,
	resource: Resource,
	isCombinedOptimization: boolean,
	loadedRunIds: Set<number>,
	fromDate: string,
	toDate: string,
	runStore: RunsStoreAdapter
) => {
	const runs = Object.values(runStore.getAll()).filter(run => {
		let filter = !loadedRunIds.has(run.id);

		if (resource === Resource.DRIVER && driverIdsToFetch.size > 0) {
			filter = filter && driverIdsToFetch.has(run.user_id);
		}

		if (resource === Resource.VEHICLE && isCombinedOptimization && vehicleIdsToFetch.size > 0) {
			filter = filter && vehicleIdsToFetch.has(run.vehicle_id);
		}

		return filter && isBetween(run.scheduled_start_time, [fromDate, toDate]);
	});

	const driverIdsNotCached = getNotCachedIds(runs, 'user_id', driverIdsCached);
	const vehicleIdsNotCached = isCombinedOptimization
		? getNotCachedIds(runs, 'vehicle_id', vehicleIdsCached)
		: new Set<number>();
	runs.forEach(run => loadedRunIds.add(run.id));

	if (driverIdsNotCached.size > 0) {
		driverIdsNotCached.forEach(driverId => driverIdsCached.add(driverId));

		getConsecutiveRunsByResource(
			driverIdsNotCached,
			vehicleIdsNotCached,
			driverIdsCached,
			vehicleIdsCached,
			Resource.DRIVER,
			isCombinedOptimization,
			loadedRunIds,
			fromDate,
			toDate,
			runStore
		);
	}

	if (vehicleIdsNotCached.size > 0) {
		vehicleIdsNotCached.forEach(vehicleId => vehicleIdsCached.add(vehicleId));

		getConsecutiveRunsByResource(
			driverIdsNotCached,
			vehicleIdsNotCached,
			driverIdsCached,
			vehicleIdsCached,
			Resource.VEHICLE,
			isCombinedOptimization,
			loadedRunIds,
			fromDate,
			toDate,
			runStore
		);
	}
};

const getTasksMapById = (ids: number[], tasksStore: TasksStoreAdapter): Map<number, Task> => {
	const tasks = ids.map(taskId => routesPlannerTasksConverter.convert(tasksStore.get(taskId)));

	return new Map(tasks.map(task => [task.id, task]));
};

const getMinAndMaxTimeOfRuns = (runs: RoutesPlannerRun[], routesPlannerConfig: RoutesPlannerConfiguration) => {
	const runsTimes = runs
		.map(run => dateUtils.getTimeFromUnixTime(run.depot_etl))
		.sort((a: number, b: number) => a - b);

	const bufferInHours: number = routesPlannerConfig.timelineConfig.axisBoundaryBufferHours;

	return {
		from: dateUtils.subtract(runsTimes[0], bufferInHours, 'h').toISOString(),
		to: dateUtils.add(runsTimes[runsTimes.length - 1], bufferInHours, 'h').toISOString()
	};
};

function getNotCachedIds(runs: Run[], field: 'user_id' | 'vehicle_id', cachedIds: Set<number>): Set<number> {
	const ids = Array.from(new Set(runs.map(run => run[field]).filter(Boolean)));
	return new Set(ids.filter(id => id && !cachedIds.has(id)));
}

export const consecutiveRoutesLoader = { load };
